import { useEffect, useState } from "react";
import { logsApi, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Card, Empty, Loader, Pagination, Select } from "../components/ui";
import { changedFields, LOG_ACTION_LABEL, LOG_ENTITY_LABEL } from "../utils/copy";

const formatWhen = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

export default function Logs() {
  const { query, ready } = useBuilderQuery();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!ready) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await logsApi.list({ ...query, entityType, action, page, limit: 20 });
      const data = unwrapList(res);
      setItems(data.items);
      setPagination(data.pagination);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, entityType, action, query.builderId]);

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">Before and after every change</p>
          <h1>Activity log</h1>
        </div>
      </header>

      <div className="filters">
        <Select
          value={entityType}
          onChange={(event) => {
            setEntityType(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All records</option>
          <option value="expense">Expenses</option>
          <option value="sale">Sales</option>
          <option value="installment">Installments</option>
          <option value="vendor">Vendors</option>
          <option value="vendor_payment">Vendor payments</option>
          <option value="bank">Banks</option>
        </Select>
        <Select
          value={action}
          onChange={(event) => {
            setAction(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All actions</option>
          <option value="create">Added</option>
          <option value="update">Changed</option>
          <option value="delete">Removed</option>
        </Select>
      </div>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty title="No logs yet" text="Add, edit or delete a sale or expense and it will show here." />
      ) : (
        <div className="stack">
          {items.map((log) => {
            const rows = changedFields(log.before, log.after);
            return (
              <Card key={log.id} className="log-card">
                <div className="card-head">
                  <h3>
                    {LOG_ACTION_LABEL[log.action] || log.action}{" "}
                    {LOG_ENTITY_LABEL[log.entityType] || log.entityType}
                  </h3>
                  <span className={`pill pill-${log.action}`}>{LOG_ACTION_LABEL[log.action]}</span>
                </div>
                <p className="muted">
                  {log.actorEmail} · {formatWhen(log.createdAt)}
                </p>
                {rows.length === 0 ? (
                  <p className="muted">{log.action === "create" ? "New record added." : "Record removed."}</p>
                ) : (
                  <div className="diff-table">
                    <div className="diff-row diff-head">
                      <span>Field</span>
                      <span>Before</span>
                      <span>After</span>
                    </div>
                    {rows.map((row) => (
                      <div key={row.key} className="diff-row">
                        <span>{row.field}</span>
                        <span>{row.from}</span>
                        <span>{row.to}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={setPage} />
    </div>
  );
}
